'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import ComboBox, { ComboBoxOptionsSlot } from '@pixum/combobox';
import type { PickupPointSelection, PickupPointSelectorProps } from '../../types';
import styles from '../../CartAddressStep.module.css';
import { PICKUP_POINT_INPUT_NAME } from './constants';
import { usePickupPointFocusGuard, usePickupPointListSearch } from './hooks';
import { PickupPointOptionsList } from './partials/PickupPointOptionsList';
import { PickupPointSkeleton } from './partials/PickupPointSkeleton';
import { discardFailedPickupPoints, seedPickupPoints } from './utils';

/**
 * Loads pickup locations and reports the selected location to the address form.
 * @param props - Search values, localized labels, current selection, and selection callback.
 * @returns The pickup-location search controls and result selector.
 */
export function PickupPointSelector({
  city,
  constants,
  bottomSheetBackdropClassName,
  error,
  onChange,
  initialPickupPoints,
  selectedStation,
  zip
}: PickupPointSelectorProps) {
  const selectorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const isDisabled = !city.trim() && !zip.trim();
  const visibleError = isDisabled ? undefined : error;
  const search = usePickupPointListSearch({ city, zip, isOpen });
  const { focusTriggerAfterClose, handleFocusCapture, handleKeyDownCapture } = usePickupPointFocusGuard({
    selectorRef,
    isOpen,
    setIsOpen
  });

  // Close when the field becomes disabled, otherwise it would pop open again once it is enabled.
  if (isDisabled && isOpen) {
    setIsOpen(false);
  }

  useEffect(() => {
    if (initialPickupPoints) {
      seedPickupPoints(initialPickupPoints.search, initialPickupPoints.promise);
    }
  }, [initialPickupPoints]);

  useEffect(() => {
    if (!isOpen) {
      discardFailedPickupPoints();
    }
  }, [isOpen]);

  const handlePickupPointChange = (selection: PickupPointSelection) => {
    onChange(selection);
    setIsOpen(false);
    focusTriggerAfterClose();
  };

  return (
    <div
      className={styles.cartPickupPointSelector}
      onFocusCapture={handleFocusCapture}
      onKeyDownCapture={handleKeyDownCapture}
      ref={selectorRef}
    >
      <ComboBox
        bottomSheetBackdropClassName={bottomSheetBackdropClassName}
        className={styles.cartPickupPointCombobox}
        disabled={isDisabled}
        hasError={Boolean(visibleError)}
        helperText={visibleError}
        headline={constants['i18n_pickup_location']}
        isOpen={isOpen}
        label={constants['i18n_pickup_location']}
        name={PICKUP_POINT_INPUT_NAME}
        noResultsText={constants['i18n_no_results_found']}
        onChange={setQuery}
        onOutsideClick={() => setIsOpen(false)}
        options={[]}
        optionsSlot={
          <ComboBoxOptionsSlot
            aria-label={constants['i18n_pickup_location']}
            className="scrollable"
            id="pickup-point-options"
            role="listbox"
            tag="ul"
          >
            <Suspense fallback={<PickupPointSkeleton constants={constants} />}>
              <PickupPointOptionsList
                city={search.city}
                constants={constants}
                onChange={handlePickupPointChange}
                query={query}
                selectedStation={selectedStation}
                zip={search.zip}
              />
            </Suspense>
          </ComboBoxOptionsSlot>
        }
        setIsOpen={setIsOpen}
        value={selectedStation}
        withSearch
        placeholder={constants['i18n_pickup_point_search']}
      />
    </div>
  );
}
